import AppsIcon from "@mui/icons-material/Apps";
import DeselectIcon from "@mui/icons-material/Deselect";
import PublishIcon from "@mui/icons-material/Publish";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextareaAutosize,
  Tooltip,
  Typography,
} from "@mui/material";
import lodash from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllFeatureFiles } from "src/clients/FeatureService";
import { sendReviewRequestMail } from "src/clients/MailService";
import {
  getAllPublishData,
  publishFeatureFiles as publishFeaturesToReview,
  savePublishedData,
} from "src/clients/PublishService";
import { getClientDetails } from "src/redux/slices/clientDetailsSlice";
import {
  getUserFeatureFiles,
  userFilesUpdate,
} from "src/redux/slices/userFilesSlice";
import { login, selectUser } from "src/redux/slices/userSlice";
import { PublishData, PublishFeatures } from "src/types/PublishData";
import { PublishRequestStatus } from "src/types/PublishRequestStatus";
import { Feature } from "../../types/Feature";
import { LOCATION, TOAST_MESSAGE } from "../constants/Constants";
import WarningPopup from "../modal/WarningPopup";
import NotifyError from "../notifications/NotifyError";
import NotifySuccess from "../notifications/NotifySuccess";
import GridFeatureCard from "../templates/GridFeatureCard";
import ListFeatureCard from "../templates/ListFeatureCard";
import Pagination from "../templates/Pagination";
import publishstyles from "./Publish.module.css";

const Publish = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [isDisable, isLoading] = useState<boolean>(false);
  const [isGridMode, setIsGridMode] = useState<boolean>(true);
  const [commentPopup, setCommentPopup] = useState<boolean>();
  const [PRComment, setPRComment] = useState<string>("");
  const publishData: PublishData = {
    publishStatus: "",
    product: "",
    updatedBy: "",
    updatedTimeStamp: "",
    createdBy: "",
    createdTimeStamp: "",
    publishId: 0,
    clientId: 0,
    publishFeatures: [],
  };
  const user = useSelector(selectUser);
  const clients = useSelector(getClientDetails);
  const dispatch = useDispatch();
  const userfiles = useSelector(getUserFeatureFiles) || [];
  const [featureData, setFeatureData] = useState<Feature[] | undefined>(() => {
    if (userfiles.userFiles === undefined) {
      return undefined;
    } else {
      return userfiles.userFiles;
    }
  });
  const fetchData = async () => {
    dispatch(
      login({
        userDetailsId: user.userDetailsId,
        userId: user.userId,
        password: user.password,
        email: user.email,
        role: user.role,
        permissionsList: user.permissionsList,
        productCode: user.productCode,
        firstName: user.firstName,
        lastName: user.lastName,
        products: user.products,
        filesStatus: true,
        clientId: user.clientId,
        clientCode: user.clientCode,
        selectedClient: user.selectedClient,
        auth: user.auth,
      })
    );
    const featuresRes = await getAllFeatureFiles(
      [LOCATION.IN_PROGRESS, LOCATION.REVIEW],
      user.auth,
      user.productCode,
      user.selectedClient
    );
    if (featuresRes.ok) {
      const data = featuresRes.json();
      const featureFilesData = await data;
      const validFeatureList = featureFilesData.features;
      const userArray = [
        ...validFeatureList[LOCATION.IN_PROGRESS],
        ...validFeatureList[LOCATION.REVIEW],
      ];
      dispatch(
        userFilesUpdate({
          userFiles: userArray,
        })
      );
      setFeatureData(userArray);
    } else if (featuresRes.status === 408) {
      setFeatureData([]);
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.FAILED_TO_CONNECT}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else {
      setFeatureData([]);
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
    dispatch(
      login({
        userDetailsId: user.userDetailsId,
        userId: user.userId,
        password: user.password,
        email: user.email,
        role: user.role,
        permissionsList: user.permissionsList,
        productCode: user.productCode,
        firstName: user.firstName,
        lastName: user.lastName,
        products: user.products,
        filesStatus: false,
        clientId: user.clientId,
        clientCode: user.clientCode,
        selectedClient: user.selectedClient,
        auth: user.auth,
      })
    );
  };
  useEffect(() => {
    setFeatureData(undefined);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.productCode, user.selectedClient]);

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleSelect = (fileName: string) => {
    const index = selectedFeatures.indexOf(fileName);
    if (index !== -1) {
      const updatedFeatures = [...selectedFeatures];
      updatedFeatures.splice(index, 1);
      setSelectedFeatures(updatedFeatures);
    } else {
      setSelectedFeatures([...selectedFeatures, fileName]);
    }
  };

  const getCurrentSelectList = () => {
    const currentList: string[] = [];
    currentPageItems &&
      currentPageItems.forEach((file) => {
        if (file.location !== LOCATION.REVIEW) {
          currentList.push(file.name.replaceAll(" ", "_"));
        }
      });
    return currentList;
  };

  const handleSelectAll = () => {
    const currentList: string[] = getCurrentSelectList();
    const newSelectedFeatures = currentList.filter(
      (fileName) => !selectedFeatures.includes(fileName)
    );
    setSelectedFeatures([...selectedFeatures, ...newSelectedFeatures]);
  };
  const handleDeSelectAll = () => {
    const currentList: string[] = getCurrentSelectList();
    const updatedSelectedFeatures = selectedFeatures.filter(
      (fileName) => !currentList.includes(fileName)
    );
    setSelectedFeatures(updatedSelectedFeatures);
  };

  const handleOnViewModeChange = (viewMode: string) => {
    if (viewMode === "GRID") {
      setIsGridMode(true);
    } else {
      setIsGridMode(false);
    }
  };

  const handleCommentOk = async () => {
    setCommentPopup(false);
    isLoading(true);
    const publishDataDtoList = await (
      await getAllPublishData(
        user.productCode,
        user.selectedClient === null
          ? user.clientId
          : clients.find(
              (client: any) => client.clientCode === user.selectedClient
            ).clientId,
        user.auth
      )
    ).json();

    let inproressMessage = "";
    let approvemessage = "";
    lodash.some(publishDataDtoList, (publishDataDto) => {
      if (
        lodash.includes(selectedFeatures, publishDataDto.featureName) &&
        publishDataDto.featureStatus === PublishRequestStatus.IN_PROGRESS
      ) {
        inproressMessage = inproressMessage + publishDataDto.featureName + ",";
      }
      if (
        lodash.includes(selectedFeatures, publishDataDto.featureName) &&
        publishDataDto.featureStatus === PublishRequestStatus.APPROVE
      ) {
        approvemessage = approvemessage + publishDataDto.featureName + ",";
      }
    });
    if (inproressMessage.length > 0 || approvemessage.length > 0) {
      if (inproressMessage.length > 0) {
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">
              {inproressMessage.substring(0, inproressMessage.length - 1)}
              test script(s) are already in review
            </Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      }
      if (approvemessage.length > 0) {
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">
              {approvemessage.substring(0, approvemessage.length - 1)}
              test script(s) are already approved
            </Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      }
      isLoading(false);
    } else {
      const publishFeaturesToReviewResponse = await publishFeaturesToReview(
        selectedFeatures,
        user.productCode,
        user.auth,
        user.selectedClient
      );
      if (publishFeaturesToReviewResponse.ok) {
        publishData.publishStatus = PublishRequestStatus.CREATED;
        publishData.publishComment = PRComment;
        publishData.product = user.productCode;
        publishData.clientId =
          user.selectedClient === null
            ? user.clientId
            : clients.find(
                (client: any) => client.clientCode === user.selectedClient
              ).clientId;
        for (let i = 0; i < selectedFeatures.length; i++) {
          const newPublishFeature: PublishFeatures = {
            clientId:
              user.selectedClient === null
                ? user.clientId
                : clients.find(
                    (client: any) => client.clientCode === user.selectedClient
                  ).clientId,
            featureName: selectedFeatures[i],
            featureStatus: PublishRequestStatus.IN_PROGRESS,
            featureComment: "",
          };
          publishData.publishFeatures.push(newPublishFeature);
        }
        const savePublishedDataResponse = await savePublishedData(
          publishData,
          user.auth
        );
        if (savePublishedDataResponse.ok) {
          let successmessage = "";
          selectedFeatures.forEach((feature: string) => {
            successmessage =
              successmessage + feature.replaceAll(" ", "_") + ", ";
          });
          const mailResponse = await sendReviewRequestMail(
            selectedFeatures,
            publishData.clientId,
            user.auth
          );
          if (mailResponse.ok) {
            fetchData();
            toast.success(
              <NotifySuccess header={"Success"}>
                <Typography variant="body2">
                  {successmessage.substring(0, successmessage.length - 2)}
                  published successfully
                </Typography>
              </NotifySuccess>,
              {
                icon: false,
                closeButton: false,
              }
            );
            setSelectedFeatures([]);
            isLoading(false);
          } else {
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  Failed to send Publish mail
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
            isLoading(false);
          }
        } else {
          toast.error(
            <NotifyError header={"Error"}>
              <Typography variant="body2">
                Failed to save PublishData
              </Typography>
            </NotifyError>,
            {
              icon: false,
              closeButton: false,
            }
          );
          isLoading(false);
        }
      } else if (publishFeaturesToReviewResponse.status === 408) {
        isLoading(false);
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">
              {TOAST_MESSAGE.FAILED_TO_CONNECT}
            </Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      } else {
        isLoading(false);
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">
              {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
            </Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      }
      setPRComment("");
    }
  };

  const handleCommentCancel = () => {
    setCommentPopup(false);
    setPRComment("");
  };

  const handlePublish = async () => {
    setCommentPopup(true);
  };

  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;
  const currentPageItems = featureData?.slice(firstIndex, lastIndex);

  if (!featureData) {
    return (
      <Box className="loaderIconStylings">
        <CircularProgress disableShrink />
      </Box>
    );
  }
  return (
    <Box>
      {isDisable && <Box className={publishstyles.overlay}></Box>}
      {isDisable && (
        <Box className={publishstyles.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      )}
      {!lodash.isEmpty(currentPageItems) ? (
        <Box>
          <Box
            sx={{
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
              paddingTop: "2.5%",
            }}
          >
            <Box></Box>
            <Typography variant="h5">Publish Test Scripts</Typography>
            <Box sx={{ display: "flex" }}>
              <Box
                sx={{
                  visibility:
                    selectedFeatures.length > 0 ? "visible" : "hidden",
                }}
              >
                {currentPageItems &&
                currentPageItems
                  .filter((item) => item.location !== LOCATION.REVIEW)
                  .every((item) =>
                    selectedFeatures.includes(item.name.replaceAll(" ", "_"))
                  ) ? (
                  <Tooltip
                    title="Deselectall"
                    placement="bottom"
                  >
                    <IconButton
                      size="large"
                      disableRipple={true}
                      onClick={handleDeSelectAll}
                    >
                      <DeselectIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="Selectall"
                    placement="bottom"
                  >
                    <IconButton
                      size="large"
                      disableRipple={true}
                      onClick={handleSelectAll}
                    >
                      <SelectAllIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  marginRight: "1rem",
                }}
              >
                {!isGridMode ? (
                  <Tooltip
                    title="Grid View"
                    placement="bottom"
                  >
                    <IconButton
                      size="large"
                      onClick={() => handleOnViewModeChange("GRID")}
                    >
                      <AppsIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="List View"
                    placement="bottom"
                  >
                    <IconButton
                      size="large"
                      onClick={() => handleOnViewModeChange("VIEW")}
                    >
                      <ViewHeadlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
          <Box className={publishstyles.featureItemsOuterBox}>
            {currentPageItems?.map((file: Feature, key: number) => {
              return isGridMode ? (
                <GridFeatureCard
                  key={key}
                  id={key}
                  feature={file}
                  totalSelected={selectedFeatures.length}
                  handleSelect={handleSelect}
                  isChecked={
                    selectedFeatures.indexOf(file.name.replaceAll(" ", "_")) !==
                    -1
                  }
                  actions={[]}
                />
              ) : (
                <ListFeatureCard
                  key={key}
                  id={key}
                  feature={file}
                  totalSelected={selectedFeatures.length}
                  handleSelect={handleSelect}
                  isChecked={
                    selectedFeatures.indexOf(file.name.replaceAll(" ", "_")) !==
                    -1
                  }
                  actions={[]}
                />
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box className={publishstyles.NoFilesMessage}>
          <Typography variant="subtitle1">No Test Scripts present</Typography>
        </Box>
      )}
      <Box className={publishstyles.actionBox}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {selectedFeatures.length > 0 && (
            <Typography
              sx={{
                margin: "6px 1rem 0rem 0rem",
                color: "#191F1C",
              }}
            >
              Selected : ({selectedFeatures.length})
            </Typography>
          )}
          {/* pagination */}
          <Pagination
            rows={String(itemsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalDataLength={featureData?.length}
            dataPerPage={itemsPerPage}
            handleItemsPerPageChange={(e) => handleItemsPerPageChange(e)}
            fieldName="Test scripts"
          />
        </Box>
        {/* action buttons */}
        <Button
          variant="outlined"
          startIcon={<PublishIcon />}
          onClick={handlePublish}
          disabled={selectedFeatures.length === 0}
        >
          Publish
        </Button>
      </Box>
      {commentPopup && (
        <WarningPopup
          isOpen={true}
          onClose={handleCommentCancel}
          actionButtons={[
            {
              name: "Confirm",
              onButtonClick: handleCommentOk,
              isDisabled: PRComment === "",
            },
            {
              name: "Cancel",
              onButtonClick: handleCommentCancel,
            },
          ]}
        >
          <Typography sx={{ paddingBottom: "5%" }}>
            Please add commit message for the PR.
          </Typography>
          <TextareaAutosize
            style={{ width: "102%", height: "90px" }}
            required
            id="PRcomment"
            maxRows={10}
            minRows={4}
            placeholder="Enter comment"
            value={PRComment}
            onChange={(e) => setPRComment(e.target.value)}
          />
        </WarningPopup>
      )}
    </Box>
  );
};

export default Publish;
